import {
  FaCheckCircle,
  FaClipboardCheck,
  FaFileInvoiceDollar,
  FaRegClock,
  FaShieldAlt,
  FaTasks,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";

const seo = {
  title: "Denial Management Services for Healthcare Providers",
  description:
    "Physicians Success Partners (PSP) provides expert denials management services, partnering closely with healthcare providers.",
  keywords:
    "denial management, claim appeals, revenue recovery, denied claims follow-up",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Denial Management Services",
      url: "https://psprcm.us/services/denial-management/",
      description:
        "Physicians Success Partners (PSP) provides expert denials management services, partnering closely with healthcare providers.",
      serviceType: "Denial Management",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Denial Management Solutions",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Denial Analysis" },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Claim Correction & Resubmission",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Appeals & Reconsiderations",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Accounts Receivable (AR) Follow-Up",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Root Cause Analysis & Denial Prevention",
            },
          },
        ],
      },
    },
    createBreadcrumbSchema(
      "/services/denial-management",
      "Denial Management Experts",
    ),
  ],
};

export default function DenialManagement() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/denial-management"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
          heading: "Denial Management Services for Healthcare Providers",
          paragraph:
            "Physicians Success Partners (PSP) provides expert denials management services, partnering closely with healthcare providers to recover lost revenue, resolve claim rejections, and prevent future denials before they impact your bottom line.",
          image: "/img/services-img/denial-management.webp",
        }}
        grid={{
          kicker: "Recovery Focus",
          heading:
            "Denials Management Services That Help You Recover Revenue Faster",
          description: `At Physicians Success Partners (PSP), our denial management services are built around identifying the root cause of every rejected claim and resolving it with speed and accuracy. Our certified denial management specialist team brings deep expertise across multiple payer types, specialties, and denial categories including coding errors, authorization issues, and medical necessity disputes.
          We analyze every denial, build a structured appeal strategy, and implement prevention workflows tailored to your practice,  so your revenue stays protected and your cash flow remains consistent.
            `,
          metrics: [
            {
              icon: <FaFileInvoiceDollar />,
              value: "20+",
              label: "Years of Combined Denial Management Experience",
              style: "metricPrimary",
            },
            {
              icon: <FaClipboardCheck />,
              value: "95%",
              label: "Denial Overturn Success Rate",
              style: "metricAccent",
            },
            {
              icon: <FaCheckCircle />,
              value: "98%",
              label: "First-Pass Claim Accuracy Rate",
              style: "metricLight",
            },
            {
              icon: <FaShieldAlt />,
              value: "100%",
              label: "HIPAA Compliance",
              style: "metricDark",
            },
          ],
          panelEyebrow: "Denial Resolution",
          panelHeading:
            "Optimized for Consistent and Accurate Denial Management Outcomes at Every Stage",
          assurances: [
            {
              icon: <FaCheckCircle />,
              label: "Accurate Denial Identification & Categorization",
            },
            { icon: <FaTasks />, label: "Reliable Appeal Filing & Follow-Up" },
            { icon: <FaRegClock />, label: "Timely Reimbursement Recovery" },
          ],
        }}
        services={{
          heading:
            "PSP's Top Rated Denials Management Services Group Is Here for Your Practice",
          description:
            "PSP's denial management specialist team analyzes your entire claims and revenue cycle from initial submission to final resolution. We identify denial patterns, reduce claim rejection rates, and implement best practices that ensure compliant and accurate billing at every stage. Our structured denial management solutions approach helps practices of all sizes recover lost revenue and achieve sustainable financial growth while staying fully HIPAA compliant.",
          items: [
            {
              title: "Clean Claim Submission & Prevention",
              text: "Our denials management services organize billing and coding workflows carefully so claims go out with stronger accuracy, fewer preventable errors, and significantly better first-pass approval outcomes with every payer.",
            },
            {
              title: "Steadier Revenue Performance",
              text: "Our denial management specialist team supports payment consistency with reliable appeal follow-up, reimbursement visibility, and practical claims denial management oversight for your everyday revenue cycle operations.",
            },
            {
              title: "Reduced Administrative Burden",
              text: "By handling complex denial management services externally, we help your practice save valuable time and keep internal teams focused on patient-facing priorities rather than back-office appeal processing tasks.",
            },
            {
              title: "Compliance-Minded Execution",
              text: "We stay fully aligned with payer-specific rules, medical necessity guidelines, and coding standards so your denial management solutions process remains dependable, organized, audit-ready, and easier to manage at every level.",
            },
          ],
        }}
        faq={{
          image: "/img/services-img/denial-management.webp",
          imageAlt: "Denial management support",
          title:
            "Hire a Dedicated Denial Management Specialist to Protect Your Clinic's Revenue Cycle",
          description:
            "Our denial management teams are more than just billers. We are every USA provider's most trusted denials management services partner. Our denial management advocates work alongside physicians to identify denial trends, correct root causes, and build sustainable revenue protection strategies.",
          btnText: "Contact Us",
        }}
        specialtiesContent={{
          eyebrow: "Denial Recovery Support",
          heading:
            "Specialty-aware denial follow-up for complex reimbursement workflows.",
          description:
            "We support specialty practices with structured denial analysis, appeal coordination, and organized payer follow-up.",
        }}
      />
    </>
  );
}
